import type { QueryClient } from '@tanstack/react-query';

/**
 * Series rows cancelled in this browser session.
 *
 * The week grid drops a card only when the mounted `['appointments', 'range']`
 * result no longer contains that id. `delete_appointment_series` already
 * invalidates `['appointments']`, but the open week keeps the previous list
 * until a refetch replaces it, and a refetch can write those same rows back.
 * These rules remove the rows from loaded range caches and from later range
 * fetches until the page is reloaded.
 */
const rulesByClient = new WeakMap<QueryClient, SeriesCancelRule[]>();

export interface SeriesCancelMember {
  id: string;
  starts_at: string;
  status: string;
  is_recurrence_parent?: boolean | null;
  recurrence_parent_id?: string | null;
}

export interface SeriesCancelRule {
  anchorId: string;
  parentId: string | null;
  deleteFutureOnly: boolean;
  anchorStartsAt: string | null;
}

export function seriesCancelRuleFromMembers(
  members: SeriesCancelMember[],
  anchorId: string,
  deleteFutureOnly: boolean,
): SeriesCancelRule {
  const anchor = members.find((row) => row.id === anchorId) ?? null;
  if (!anchor) {
    return { anchorId, parentId: null, deleteFutureOnly, anchorStartsAt: null };
  }
  const parentId = anchor.is_recurrence_parent ? anchor.id : (anchor.recurrence_parent_id ?? null);
  return {
    anchorId,
    parentId,
    deleteFutureOnly,
    anchorStartsAt: anchor.starts_at,
  };
}

export function rememberSeriesCancelRule(qc: QueryClient, rule: SeriesCancelRule): void {
  const rules = rulesByClient.get(qc);
  if (rules) rules.push(rule);
  else rulesByClient.set(qc, [rule]);
}

export function seriesCancelRules(qc: QueryClient): readonly SeriesCancelRule[] {
  return rulesByClient.get(qc) ?? [];
}

/** Upcoming rows the series RPC cancels. Completed and earlier rows stay when future-only. */
export function isHiddenBySeriesCancel(
  row: SeriesCancelMember,
  rules: readonly SeriesCancelRule[],
): boolean {
  if (row.status !== 'upcoming') return false;
  const startsAt = new Date(row.starts_at).getTime();
  for (const rule of rules) {
    const inSeries =
      row.id === rule.anchorId ||
      (rule.parentId != null &&
        (row.id === rule.parentId || row.recurrence_parent_id === rule.parentId));
    if (!inSeries) continue;
    if (!rule.deleteFutureOnly || rule.anchorStartsAt == null) return true;
    if (startsAt >= new Date(rule.anchorStartsAt).getTime()) return true;
  }
  return false;
}

export function withoutSeriesCancelHidden<T extends SeriesCancelMember>(
  rows: T[],
  rules: readonly SeriesCancelRule[],
): T[] {
  if (rules.length === 0) return rows;
  return rows.filter((row) => !isHiddenBySeriesCancel(row, rules));
}

/** Keep the same array when nothing was hidden, so the calendar does not re-render. */
export function stripHiddenFromRangeData<T extends SeriesCancelMember>(
  rows: T[] | undefined,
  rules: readonly SeriesCancelRule[],
): T[] | undefined {
  if (!rows || rules.length === 0) return rows;
  const next = rows.filter((row) => !isHiddenBySeriesCancel(row, rules));
  return next.length === rows.length ? rows : next;
}

export function hideSeriesCancelFromRangeCaches(qc: QueryClient): void {
  const rules = seriesCancelRules(qc);
  if (rules.length === 0) return;
  qc.setQueriesData<SeriesCancelMember[]>({ queryKey: ['appointments', 'range'] }, (old) =>
    stripHiddenFromRangeData(old, rules),
  );
}
